import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { CASES } from "../data/content";
import { Reveal, Overline } from "./Reveal";

// Detect small screens once — used to skip heavy click/hover animations on phones.
const IS_MOBILE = typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches;

export default function CaseStudies() {
  // Unified interaction model : desktop uses hover, mobile uses tap.
  // Both drive the same `active` state — a card only reveals its face + text
  // when explicitly activated by the user.
  const [active, setActive] = useState(null);

  return (
    <section id="cas" data-testid="cases-section" className="mx-auto max-w-7xl px-5 py-20 sm:px-6 sm:py-24 lg:py-32">
      <Reveal className="mb-12 text-center sm:mb-14">
        <Overline>Études de cas</Overline>
        <h2 className="font-head mx-auto mt-4 max-w-3xl text-3xl font-extrabold text-[#0B1220] sm:text-5xl">
          Est-ce que ça <span className="text-[#2563EB]">marche</span> vraiment ?
        </h2>
        <p className="mx-auto mt-4 max-w-xl text-sm text-[#5B6B85] sm:mt-5 sm:text-base">
          Cliquez sur une étude pour découvrir le résultat.
        </p>
      </Reveal>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-0">
        {CASES.map((c, i) => {
          const isActive = active === i;
          const dimmed = active !== null && !isActive;
          const isLast = i === CASES.length - 1;
          return (
            <motion.button
              type="button"
              key={i}
              initial={IS_MOBILE ? false : { opacity: 0, y: 24 }}
              whileInView={IS_MOBILE ? undefined : { opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={IS_MOBILE ? { duration: 0 } : { duration: 0.6, delay: i * 0.1 }}
              animate={IS_MOBILE ? false : {
                scale: isActive ? 1.02 : dimmed ? 0.985 : 1,
                filter: dimmed ? "blur(2px)" : "blur(0px)",
                opacity: dimmed ? 0.6 : 1,
              }}
              onMouseEnter={IS_MOBILE ? undefined : () => setActive(i)}
              onMouseLeave={IS_MOBILE ? undefined : () => setActive((prev) => (prev === i ? null : prev))}
              onClick={IS_MOBILE ? undefined : () => setActive(i)}
              data-testid={`case-${i}`}
              aria-pressed={isActive}
              className={`group relative flex flex-col overflow-hidden rounded-2xl border border-[#E6EDF6] p-0 pb-5 text-left transition-shadow duration-300 sm:min-h-[26rem] sm:justify-end sm:overflow-visible sm:p-8 ${
                isLast ? "col-span-2 sm:col-span-1" : ""
              } ${
                isActive
                  ? "z-20 bg-white shadow-[0_22px_54px_-26px_rgba(37,99,235,0.32)] sm:rounded-2xl"
                  : "bg-white sm:rounded-none"
              }`}
            >
              {/* Founder face — top block on mobile (fixed height), full card overlay on desktop */}
              {c.face && (
                <div className="pointer-events-none relative h-44 w-full overflow-hidden sm:absolute sm:inset-0 sm:h-auto sm:w-auto sm:rounded-[inherit]">
                  <div
                    data-testid={`case-reveal-face-${i}`}
                    className={`h-full w-full transition-all duration-500 ${isActive ? "opacity-100 blur-0 grayscale-0" : "opacity-100 blur-0 grayscale-0 sm:opacity-30 sm:blur-[2px] sm:grayscale"}`}
                  >
                    <img
                      src={c.face}
                      alt=""
                      style={{
                        objectPosition: c.facePos || "50% 20%",
                        transform: c.faceZoom
                          ? `scale(${isActive ? c.faceZoom : c.faceZoom * 1.05})`
                          : undefined,
                        transformOrigin: c.faceOrigin || "center center",
                      }}
                      className={`h-full w-full object-cover transition-transform duration-[800ms] ease-out ${c.faceZoom ? "" : (isActive ? "scale-100" : "scale-105")}`}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#F5F8FC]/25 via-transparent to-transparent" />
                    {c.holo && (
                      <div className="animate-holo absolute inset-0 mix-blend-screen bg-[linear-gradient(115deg,rgba(37,99,235,0.4)_0%,rgba(34,211,238,0.32)_35%,rgba(139,92,246,0.32)_65%,rgba(37,99,235,0.4)_100%)]" />
                    )}
                  </div>
                </div>
              )}

              {/* Top badge — hidden on mobile, emerges above card on desktop */}
              <div
                className={`pointer-events-none relative z-30 mb-4 hidden justify-center transition-all duration-500 sm:absolute sm:-top-9 sm:left-1/2 sm:mb-0 sm:flex sm:-translate-x-1/2 ${
                  isActive ? "opacity-100 grayscale-0" : "opacity-40 grayscale"
                }`}
              >
                {c.logo ? (
                  <img
                    src={c.logo}
                    alt=""
                    aria-hidden="true"
                    data-testid={`case-reveal-logo-${i}`}
                    className={`w-auto object-contain ${c.logoClass || "h-12"} ${isActive ? "sm:animate-logo-glow" : ""}`}
                  />
                ) : (
                  <span
                    data-testid={`case-reveal-name-${i}`}
                    className={`font-head whitespace-nowrap text-lg font-extrabold text-[#2563EB] sm:text-2xl ${isActive ? "sm:animate-logo-glow" : ""}`}
                  >
                    {c.client}
                  </span>
                )}
              </div>

              {/* Card copy — mobile: white section below photo, desktop: absolute overlay at bottom */}
              <div
                className={`relative mt-0 px-5 pt-4 translate-y-0 opacity-100 transition-all duration-500 ease-out sm:mt-auto sm:px-0 sm:pt-0 sm:translate-y-0 sm:opacity-100`}
              >
                {/* Backdrop for text readability — hidden on mobile (photo is above), gradient on desktop */}
                <span
                  aria-hidden="true"
                  className="pointer-events-none absolute hidden sm:block sm:inset-x-[-2rem] sm:-bottom-8 sm:-top-4 sm:bg-gradient-to-t sm:from-white sm:via-white sm:to-white/60"
                />
                <p className="relative text-[11px] font-extrabold uppercase tracking-[0.16em] text-[#0B1220] sm:text-[13px] sm:tracking-[0.2em]">
                  {c.sector}
                </p>
                <div className="font-head relative mt-2 text-2xl font-extrabold tracking-tight text-[#2563EB] sm:mt-6 sm:text-4xl">
                  {c.result}
                </div>
                <h3 className="font-head relative mt-2 text-lg font-extrabold text-[#0B1220] sm:mt-4 sm:text-xl">{c.client}</h3>
                <p className="relative mt-2 text-[13px] font-medium leading-relaxed text-[#334155] sm:mt-3 sm:text-sm sm:font-semibold sm:text-[#0B1220]">{c.desc}</p>
              </div>
            </motion.button>
          );
        })}
      </div>
    </section>
  );
}
